import React, { type PropsWithChildren } from 'react';
import cx from 'classnames';
import stylesheet from './Heading.css';

export type Appearance =
  | 'heading-1'
  | 'heading-display'
  | 'heading-2'
  | 'heading-3'
  | 'heading-4'
  | 'heading-5'
  | 'heading-6';

export type Size =
  | 'heading-1'
  | 'heading-2'
  | 'heading-3'
  | 'heading-4'
  | 'heading-5'
  | 'heading-6'
  | 'display-1'
  | 'display-2'
  | 'display-3'
  | 'display-4';

export type SupportedHeading = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export type Level = 1 | 2 | 3 | 4 | 5 | 6;

export type TextWrap = 'wrap' | 'nowrap' | 'balance' | 'pretty';

export type HeadingProps = PropsWithChildren<{
  className?: string;
  /* The HTML heading element to render. Headings should be nested sequentially without skipping any levels. Learn more at https://www.w3.org/WAI/tutorials/page-structure/headings/. */
  level: Level;
  /* The visual appearance of the heading. */
  appearance?: Appearance;
  /* Change the visual appearance of the heading. */
  size?: Size;
  textWrap?: TextWrap;
}>;

const convertAppearanceOrLevelToSize = (
  level: Level,
  appearance?: Appearance,
): Size => {
  const className = appearance || `heading-${level}`;
  if (className === 'heading-1' || className === 'heading-display') {
    return 'heading-2';
  } else if (className === 'heading-2') {
    return 'heading-3';
  } else if (className === 'heading-3') {
    return 'heading-5';
  } else if (className === 'heading-4') {
    return 'heading-6';
  }
  return className;
};

/**
 * Many screen reader users navigate by heading. Add headings for each section
 * of the page, even if they must be visually hidden — as a screen reader user,
 * I want to be able to navigate through each section easily.
 */
const Heading: React.ForwardRefRenderFunction<
  HTMLHeadingElement,
  HeadingProps
> = function (
  { children, className, level, appearance, size, textWrap, ...props },
  forwardedRef,
) {
  const H: SupportedHeading = `h${level}`;
  const wrap = textWrap && stylesheet[`text-${textWrap}`];

  const renderedSize =
    size || convertAppearanceOrLevelToSize(level, appearance);

  return (
    <H
      className={cx(className, wrap, stylesheet.root, stylesheet[renderedSize])}
      ref={forwardedRef}
      {...props}
    >
      {children}
    </H>
  );
};

export default React.forwardRef(Heading);
